import React, { useEffect, useState } from 'react';
import { useWeb3React } from '@web3-react/core';
import { 
  getContractState,
  getTokenAddresses,
  getPartyAddresses,
  getAgreedNilaPrice,
  getCurrentNilaPrice,
  getLoanAmount,
  getLoanDetails,
  getContractDates,
  formatTimestamp
} from '../utils/contract';

const ContractInfo = () => {
  const { active, account } = useWeb3React();
  const [contractInfo, setContractInfo] = useState({
    state: { stateNumber: 0, stateName: 'Loading...' },
    tokens: { nilaToken: null, usdcToken: null },
    parties: { partyN: null, partyB: null, partyE: null },
    agreedPrice: '0',
    currentPrice: '0',
    loanAmount: '0',
    loanDetails: {
      principal: '0',
      nextInterestDue: 0,
      daysRemaining: 0,
      currentCollateralRatio: 0
    },
    dates: {
      executionDate: 0,
      lockEndDate: 0,
      loanDueDate: 0,
      lastInterestPayment: 0
    }
  });
  const [loading, setLoading] = useState(true);

  // Load contract information
  useEffect(() => {
    const loadContractInfo = async () => {
      setLoading(true);
      try {
        const [
          state,
          tokens,
          parties,
          agreedPrice,
          currentPrice,
          loanAmount,
          loanDetails,
          dates
        ] = await Promise.all([
          getContractState(),
          getTokenAddresses(),
          getPartyAddresses(),
          getAgreedNilaPrice(),
          getCurrentNilaPrice(),
          getLoanAmount(),
          getLoanDetails(),
          getContractDates()
        ]);

        setContractInfo({
          state,
          tokens,
          parties,
          agreedPrice,
          currentPrice,
          loanAmount,
          loanDetails,
          dates
        });
      } catch (error) {
        console.error('Error loading contract info:', error);
      } finally {
        setLoading(false);
      }
    };

    loadContractInfo();
    
    // Set up an interval to refresh data every 30 seconds
    const intervalId = setInterval(loadContractInfo, 30000);
    
    // Clean up interval on component unmount
    return () => clearInterval(intervalId);
  }, []);

  // Check if the current user is one of the parties
  const getUserRole = () => {
    if (!active || !account) return null;
    
    const { partyN, partyB, partyE } = contractInfo.parties;
    
    if (account.toLowerCase() === partyN?.toLowerCase()) return 'Party N';
    if (account.toLowerCase() === partyB?.toLowerCase()) return 'Party B';
    if (account.toLowerCase() === partyE?.toLowerCase()) return 'Party E';
    
    return null;
  };

  const userRole = getUserRole();

  return (
    <div className="contract-info">
      <h2>Contract Information</h2>
      
      {loading ? (
        <p>Loading contract information...</p>
      ) : (
        <div className="info-container">
          <div className="info-item">
            <strong>Contract State:</strong> {contractInfo.state.stateName}
          </div>
          
          <div className="info-item">
            <strong>Agreed NILA Price:</strong> ${contractInfo.agreedPrice} USD
          </div>
          
          <div className="info-item">
            <strong>Current NILA Price:</strong> ${contractInfo.currentPrice} USD
          </div>
          
          <div className="info-item">
            <strong>Loan Amount:</strong> ${contractInfo.loanAmount} USDC
          </div>
          
          {contractInfo.state.stateNumber >= 2 && ( // ACTIVE or later states
            <>
              <div className="info-item">
                <strong>Loan Principal:</strong> ${contractInfo.loanDetails.principal} USDC
              </div>
              
              <div className="info-item">
                <strong>Days Remaining:</strong> {contractInfo.loanDetails.daysRemaining}
              </div>
              
              <div className="info-item">
                <strong>Current Collateral Ratio:</strong> {contractInfo.loanDetails.currentCollateralRatio}%
              </div>
              
              <div className="info-item">
                <strong>Next Interest Due:</strong> {formatTimestamp(contractInfo.loanDetails.nextInterestDue)}
              </div>
              
              <div className="info-item">
                <strong>Execution Date:</strong> {formatTimestamp(contractInfo.dates.executionDate)}
              </div>
              
              <div className="info-item">
                <strong>Loan Due Date:</strong> {formatTimestamp(contractInfo.dates.loanDueDate)}
              </div>
            </>
          )}
          
          {active && account && userRole && (
            <div className="info-item role">
              <strong>Your Role:</strong> {userRole}
            </div>
          )}
          
          <div className="info-item">
            <strong>Contract Address:</strong> <a 
              href={`https://testnet.bscscan.com/address/0x3a27f16decd71d7030fffd1d9e9a2d58d1190d4f`}
              target="_blank"
              rel="noopener noreferrer"
            >
              0x3a27f16decd71d7030fffd1d9e9a2d58d1190d4f
            </a>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContractInfo;
